import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { IconButton, MenuItem } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Copy, EllipsisVertical, Loader2, Trash2 } from 'lucide-react';

import { createTanstackTable, toastPromise } from '../../common/utils';
import {
  useGetSharedModelsByFilter,
  useImportSharedModel,
  useRevokeSharedModel,
} from '../../hooks/tanstack/useSharedModelsHook';
import {
  useCustomNavigate,
  useGetRouteParams,
  useGetSearchResults,
} from '../../hooks/tanstack/useUtilsHook';
import routes from '../../routes';
import MenuDropdown from '../MenuDropdown';
import SearchInput from '../SearchInput';
import TanstackTable from '../TanstackTable';
import WarningDialog from '../WarningDialog';

export type DataRow = {
  id: number;
  name: string;
  description: string;
  sender: {
    email: string;
    full_name: string;
  };
  receivers: {
    email: string;
  }[];
  createdAt: string;
};

const columnHelper = createColumnHelper<DataRow>();

const columns = ({ handleSelect }: { handleSelect: (id: number) => void }) => [
  columnHelper.accessor('name', {
    cell: (info) => (
      <div className="group flex items-center gap-2 py-3 text-start">
        <p className="w-full">{info.getValue()}</p>
      </div>
    ),
    header: () => (
      <div className="flex items-start gap-2">
        <p>Model Copies</p>
      </div>
    ),
  }),
  columnHelper.accessor('description', {
    cell: (info) => (
      <div className="group flex items-center gap-2 line-clamp-2 py-3 text-start">
        <p className="w-full">{info.getValue()}</p>
      </div>
    ),
    header: () => (
      <div className="flex items-start gap-2">
        <p>Description</p>
      </div>
    ),
  }),
  columnHelper.accessor('sender', {
    id: 'sender',
    cell: (info) =>
      info.getValue() ? (
        <div className="w-full">{info.getValue().full_name}</div>
      ) : (
        <div className="text-gray-400 w-full text-start">--</div>
      ),
    header: () => <div>Shared By</div>,
  }),
  columnHelper.accessor('receivers', {
    id: 'receivers',
    cell: (info) => {
      const receivers = info.getValue();

      return receivers ? (
        <div className="w-full">{receivers.map((i) => i.email).join(', ')}</div>
      ) : (
        <div className="text-gray-400 w-full text-center">--</div>
      );
    },
    header: () => <div>Shared To</div>,
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    cell: (info) => {
      const value = info.getValue();
      const id = info.row.original.id;

      const { projectId, orgId } = useGetRouteParams();

      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

      const [openRevokeAccessDialog, setOpenRevokeAccessDialog] =
        useState(false);

      const open = Boolean(anchorEl);

      const {
        mutateAsync: importSharedModel,
        isPending: isImportingSharedModel,
      } = useImportSharedModel();

      const { mutate: revokeSharedModel, isPending: isRevokingSharedModel } =
        useRevokeSharedModel({
          onSuccess: () => {
            toastPromise({
              promise: Promise.resolve(),
              content: 'Access revoked successfully',
            });
            handleCloseDropdown();
          },
        });

      const handleImportModel = async () => {
        const res = importSharedModel({
          orgId,
          projectId,
          modelId: id,
        });

        if (res) {
          const data = await res;

          toastPromise({
            promise: res,
            content: (
              <>
                Model imported successfully.{' '}
                <Link
                  to={`/${orgId}/projects/${projectId}/models/${data.id}`}
                  className="underline cursor-pointer text-sm"
                >
                  Click here to go to the model.
                </Link>
              </>
            ),
          });
        }
        handleCloseDropdown();
      };

      const handleCloseDropdown = () => setAnchorEl(null);

      return (
        <div className="w-full flex justify-between items-center">
          <p>{value ? format(value as string, 'y/MM/dd h:mm a') : '--'}</p>
          {isImportingSharedModel || isRevokingSharedModel ? (
            <IconButton disabled>
              <Loader2 className="text-primary-bright-blue h-5 w-5 animate-spin" />
            </IconButton>
          ) : (
            <MenuDropdown
              trigger={
                <IconButton>
                  <EllipsisVertical width={20} height={20} />
                </IconButton>
              }
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              style={{
                marginTop: '5px',
              }}
              open={open}
            >
              <MenuItem
                onClick={(e) => {
                  handleImportModel();
                }}
              >
                <div className="flex items-center space-x-2">
                  <Copy size={15} />
                  <p>Import this model</p>
                </div>
              </MenuItem>
              <div className="!border-t !border-[#E4E7EC]" />
              <MenuItem
                onClick={() => {
                  setOpenRevokeAccessDialog(true);
                }}
              >
                <div className="flex items-center space-x-2 text-primary-red">
                  <Trash2 size={15} />
                  <p>Revoke access</p>
                </div>
              </MenuItem>
            </MenuDropdown>
          )}
          <WarningDialog
            open={openRevokeAccessDialog}
            content={undefined}
            title="Are you sure you want to revoke this user's access"
            primaryActionText={'Yes'}
            secondaryActionText="Cancel"
            showIcon={false}
            handleSecondaryAction={() => {
              setOpenRevokeAccessDialog(false);
            }}
            handlePrimaryAction={() => {
              revokeSharedModel({
                orgId,
                projectId,
                modelId: id,
              });
            }}
          />
        </div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.createdAt as string).getTime() -
      new Date(b.original.createdAt as string).getTime(),
    header: () => <div>Date Sent</div>,
  }),
];

const SharedModelsHistoryView = () => {
  const [data, setData] = useState<DataRow[]>([]);

  const { projectId, orgId, modelId } = useGetRouteParams();

  const form = useForm();

  const { watch } = form;

  const navigate = useCustomNavigate();

  const { data: sharedModels, isLoading: isLoadingSharedModels } =
    useGetSharedModelsByFilter(
      {
        projectId,
        orgId,
        modelId,
      },
      {
        enabled: !!projectId && !!orgId && !!modelId,
      }
    );

  const searchText = watch('search');

  const searchResults = useGetSearchResults<DataRow>({
    searchValue: searchText,
    data,
    filterFields: ['name', 'sender.full_name'],
  });

  useEffect(() => {
    if (sharedModels) {
      setData(
        sharedModels.map((r) => ({
          id: r.id,
          name: r.name,
          description: r.description,
          sender: {
            email: r.shared_by?.email,
            full_name: r.shared_by?.full_name,
          },
          receivers: r.shared_with,
          createdAt: r.created_at,
        })) as DataRow[]
      );
    }
  }, [sharedModels]);

  const handleSelect = (id: number) => {
    navigate(`${routes.projects.model.detail}?type=shared`, {
      org_id: orgId,
      project_id: projectId,
      model_id: id,
    });
  };

  const table = createTanstackTable<DataRow>({
    data: searchResults ?? data,
    columns: columns({ handleSelect }),
    defaultSorting: {
      id: 'createdAt',
      desc: true,
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold">Shared History</h1>
      <FormProvider {...form}>
        <form className="flex justify-between">
          <div className="flex w-[300px] gap-2">
            <SearchInput />
          </div>
        </form>
      </FormProvider>
      <TanstackTable
        table={table}
        handleSelect={{
          onClick: handleSelect,
          lockColIndex: 4,
        }}
        isLoading={isLoadingSharedModels}
        emptyState={searchText ? 'No results' : 'No shared model available'}
      />
    </div>
  );
};

export default SharedModelsHistoryView;
